import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Form,
  PasswordInput,
  fetchUtils,
  required,
  useLogin,
  useNotify,
} from 'react-admin';

import AuthLayout from '../AuthLayout';
import InputGroup from '../InputGroup';
import ButtonGroup from '../ButtonGroup';
import MfaEnrollmentVerify from '../../security/MfaEnrollmentVerify';
import MfaRecoveryCodes from '../../security/MfaRecoveryCodes';
import resetMfaSteps, { deriveResetMfaStep } from './resetMfaSteps';

import config from '../../../config';
import authRoutes from '../../../constants/authRoutes';
import errorNotification from '../../../utilities/errorNotification';
import { clockDriftStore } from '../../../utilities/authTokenStore';
import hasClockDrift from '../../../utilities/hasClockDrift';

const httpClient = fetchUtils.fetchJson;

type ResetFormFields = {
  password?: string;
}

type PendingEnrollment = {
  secret: string;
  enrollmentUri: string;
  pendingToken: string;
}

type ConfirmedEnrollment = {
  recoveryCodes: string[];
  token: string;
  refreshToken?: string;
}

const MfaResetPage = () => {
  const [pendingEnrollment, setPendingEnrollment] = useState<PendingEnrollment | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedEnrollment | null>(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();

  const token = new URLSearchParams(location.search).get('token');

  const backToSignIn = useCallback(() => {
    navigate(authRoutes.LOGIN, { replace: true });
  }, [navigate]);

  const startReset = useCallback(async ({ password }: ResetFormFields) => {
    if (!token || !password) return;

    setLoading(true);
    try {
      const { json } = await httpClient(`${config.apiUrl}/auth/mfa/reset`, {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setPendingEnrollment(json as PendingEnrollment);
    } catch (error) {
      notify(errorNotification(error, 'Could not start the setup. The reset link is most likely expired or already used.'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify, setLoading, setPendingEnrollment, token]);

  const confirmReset = useCallback(async (code: string) => {
    if (!pendingEnrollment) return;

    setLoading(true);
    try {
      const { json } = await httpClient(`${config.apiUrl}/auth/mfa/enroll/confirm`, {
        method: 'POST',
        body: JSON.stringify({ pendingToken: pendingEnrollment.pendingToken, code }),
      });
      const confirmedJson = json as ConfirmedEnrollment & { clockDriftSteps?: number };
      if (hasClockDrift(confirmedJson)) {
        clockDriftStore.setToken(String(confirmedJson.clockDriftSteps));
      }
      setConfirmed(confirmedJson);
    } catch (error) {
      notify(errorNotification(error, 'Could not verify the code'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify, pendingEnrollment, setConfirmed, setLoading]);

  const finishReset = useCallback(async () => {
    if (!confirmed) return;

    const { token: sessionToken, refreshToken } = confirmed;
    await login({ token: sessionToken, refreshToken });
  }, [confirmed, login]);

  useEffect(() => {
    if (!token) navigate(authRoutes.LOGIN, { replace: true });
  }, [navigate, token]);

  if (!token) return null;

  const step = deriveResetMfaStep({
    hasRecoveryCodes: !!confirmed,
    hasPendingEnrollment: !!pendingEnrollment,
  });

  const collectingPassword = step === resetMfaSteps.PASSWORD;

  return (
    <AuthLayout>
      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Reset two-step verification
      </Typography>
      <Typography
        component="h2"
        variant="body1"
        align="center"
        sx={collectingPassword ? undefined : { mb: 4 }}
      >
        {collectingPassword
          ? 'Confirm your password to continue'
          : 'Set up a new authenticator app to finish the reset your administrator started.'}
      </Typography>
      {step === resetMfaSteps.RECOVERY_CODES && confirmed && (
        <MfaRecoveryCodes
          centered
          recoveryCodes={confirmed.recoveryCodes}
          onAcknowledge={finishReset}
        />
      )}
      {step === resetMfaSteps.PAIR && pendingEnrollment && (
        <MfaEnrollmentVerify
          centered
          loading={loading}
          secret={pendingEnrollment.secret}
          enrollmentUri={pendingEnrollment.enrollmentUri}
          confirmLabel="Verify and sign in"
          onConfirm={confirmReset}
          onCancel={backToSignIn}
        />
      )}
      {collectingPassword && (
        <Form onSubmit={startReset}>
          <InputGroup>
            <PasswordInput
              source="password"
              label="Password"
              disabled={loading}
              fullWidth
              validate={[required()]}
            />
          </InputGroup>
          <ButtonGroup>
            <Button variant="contained" type="submit" disabled={loading}>
              Continue
            </Button>
          </ButtonGroup>
        </Form>
      )}
    </AuthLayout>
  );
};

export default MfaResetPage;
